import React, { useState, useMemo, useEffect } from "react";
import { useAppSelector, useAppDispatch } from "../../../store/hooks";
import { formatCurrency, formatNumber } from "../../../utils/formatting";

import { selectBreakawaySections, selectTotalTonnage } from "../../../store/selectors/breakawayHullSelectors";
import { setSectionFuelAllocation } from "../../../store/features/breakawayHullSlice";
import { calculateBreakawayFuelRequirements } from "../../../utils/calculations/breakawayCalculations";
import { calculateJumpFuelConsumption, calculatePowerPlantFuelConsumption } from "../../../utils/calculations/fuelCalculations";
import { 
  setPowerPlantReserveHours, 
  setJumpDriveReserveParsecs, 
  setReactionDriveHours 
} from "../../../store/features/shipBuilderSlice";

interface FuelDistribution {
  [sectionId: string]: {
    percentage: number;
    tons: number;
    reactionDrive: number;
    jumpDrive: number;
    powerPlant: number;
    total: number;
  };
}

/**
 * Breakaway Fuel Step
 * Allows users to configure fuel allocation for the entire breakaway hull system
 * and distribute it across sections proportionally or manually
 */
export const BreakawayFuelStep: React.FC = () => {
  const dispatch = useAppDispatch();
  
  // Get ship data
  const drives = useAppSelector((state) => state.shipBuilder.drives);
  const powerPlant = useAppSelector((state) => state.shipBuilder.powerPlant);
  const fuelTanks = useAppSelector((state) => state.shipBuilder.fuelTanks) || 0;
  const jumpDriveReserveParsecs = useAppSelector((state) => state.shipBuilder.jumpDriveReserveParsecs);
  const reactionDriveHours = useAppSelector((state) => state.shipBuilder.reactionDriveHours) || 1;
  const powerPlantReserveHours = useAppSelector((state) => state.shipBuilder.powerPlantReserveHours) || 168; // Default to 1 week
  
  // Convert reaction drive hours to weeks for consistent calculations
  const weeks = Math.ceil(reactionDriveHours / 168); // Convert hours to weeks, rounding up
  const powerPlantWeeks = Math.ceil(powerPlantReserveHours / 168); // Convert power plant reserve hours to weeks

  // Get breakaway hull data
  const sections = useAppSelector(selectBreakawaySections);
  const totalTonnage = useAppSelector(selectTotalTonnage);

  // State for fuel configuration
  const [totalFuelTons, setTotalFuelTons] = useState<number>(fuelTanks);
  const [useManualQuantity, setUseManualQuantity] = useState(false);
  const [distributionMode, setDistributionMode] = useState<'auto' | 'manual'>('auto');
  const [fuelDistribution, setFuelDistribution] = useState<FuelDistribution>({});

  // Calculate fuel requirements for the entire ship
  const fuelRequirements = useMemo(() => {
    return calculateBreakawayFuelRequirements(
      totalTonnage,
      sections,
      {
        drives,
        powerPlant,
        weeks: powerPlantWeeks, // Use power plant weeks for fuel calculations
        jumpDriveReserveParsecs,
      }
    );
  }, [
    totalTonnage,
    sections,
    drives,
    powerPlant,
    powerPlantWeeks,
    jumpDriveReserveParsecs,
  ]);

  // Calculate automatic distribution based on section sizes
  const autoDistribution = useMemo(() => {
    if (sections.length === 0) return {};

    const distribution: FuelDistribution = {};
    sections.forEach(section => {
      const sectionPercentage = Math.round((section.size / totalTonnage) * 100 * 10) / 10;
      const sectionTons = Math.round((totalFuelTons * section.size) / totalTonnage);
      
      // Distribute fuel proportionally based on section size
      const sectionReactionDrive = Math.round((fuelRequirements.reactionDrive * section.size) / totalTonnage);
      const sectionJumpDrive = Math.round((fuelRequirements.jumpDrive * section.size) / totalTonnage);
      const sectionPowerPlant = Math.round((fuelRequirements.powerPlant * section.size) / totalTonnage);
      const sectionTotal = sectionReactionDrive + sectionJumpDrive + sectionPowerPlant;

      distribution[section.id] = {
        percentage: sectionPercentage,
        tons: sectionTons,
        reactionDrive: sectionReactionDrive,
        jumpDrive: sectionJumpDrive,
        powerPlant: sectionPowerPlant,
        total: sectionTotal,
      };
    });

    return distribution;
  }, [sections, totalTonnage, totalFuelTons, fuelRequirements]);

  // Initialize distribution
  useEffect(() => {
    if (distributionMode === 'auto') {
      setFuelDistribution(autoDistribution);
    }
  }, [distributionMode, autoDistribution]);

  // Calculate distribution totals
  const distributionTotals = useMemo(() => {
    const totalPercentage = Object.values(fuelDistribution).reduce((sum, dist) => sum + dist.percentage, 0);
    const totalTons = Object.values(fuelDistribution).reduce((sum, dist) => sum + dist.tons, 0);
    const totalReactionDrive = Object.values(fuelDistribution).reduce((sum, dist) => sum + dist.reactionDrive, 0);
    const totalJumpDrive = Object.values(fuelDistribution).reduce((sum, dist) => sum + dist.jumpDrive, 0);
    const totalPowerPlant = Object.values(fuelDistribution).reduce((sum, dist) => sum + dist.powerPlant, 0);
    const totalFuel = Object.values(fuelDistribution).reduce((sum, dist) => sum + dist.total, 0);

    // More lenient validation to account for rounding effects
    const isValid = Math.abs(totalPercentage - 100) <= 0.1; // Allow up to 0.1% deviation

    return {
      totalPercentage,
      totalTons,
      totalReactionDrive,
      totalJumpDrive,
      totalPowerPlant,
      totalFuel,
      isOverAllocated: totalPercentage > 100.1,
      isUnderAllocated: totalPercentage < 99.9,
      isValid,
    };
  }, [fuelDistribution]);

  // Handle fuel quantity change
  const handleFuelQuantityChange = (quantity: number) => {
    setTotalFuelTons(quantity);
  };

  // Handle distribution mode change
  const handleDistributionModeChange = (mode: 'auto' | 'manual') => {
    setDistributionMode(mode);
    if (mode === 'auto') {
      setFuelDistribution(autoDistribution);
    }
  };

  // Handle power plant reserve weeks change
  const handlePowerPlantReserveChange = (newWeeks: number) => {
    const newHours = newWeeks * 168; // Convert weeks to hours
    dispatch(setPowerPlantReserveHours(newHours));
  };

  // Handle jump drive reserve parsecs change
  const handleJumpDriveReserveChange = (newParsecs: number) => {
    dispatch(setJumpDriveReserveParsecs(newParsecs));
  };

  // Handle reaction drive hours change
  const handleReactionDriveHoursChange = (newHours: number) => {
    dispatch(setReactionDriveHours(newHours));
  };

  // Handle manual distribution change
  const handleDistributionChange = (sectionId: string, field: 'percentage' | 'tons', value: number) => {
    if (distributionMode !== 'manual') return;

    const newDistribution = { ...fuelDistribution };
    const section = newDistribution[sectionId];
    
    if (field === 'percentage') {
      section.percentage = Math.max(0, Math.min(100, value));
      section.tons = Math.round((totalFuelTons * section.percentage) / 100);
    } else if (field === 'tons') {
      section.tons = Math.max(0, Math.min(totalFuelTons, Math.round(value)));
      section.percentage = Math.round((section.tons / totalFuelTons) * 100 * 10) / 10;
    }

    // Recalculate fuel breakdown based on section requirements
    const sectionRequirements = calculateSectionFuelRequirements(
      sections.find(s => s.id === sectionId)!,
      powerPlantWeeks
    );
    
    // Distribute the allocated tons proportionally among fuel types
    const totalRequired = sectionRequirements.reactionDrive + sectionRequirements.jumpDrive + sectionRequirements.powerPlant;
    if (totalRequired > 0) {
      section.reactionDrive = Math.round((section.tons * sectionRequirements.reactionDrive) / totalRequired);
      section.jumpDrive = Math.round((section.tons * sectionRequirements.jumpDrive) / totalRequired);
      section.powerPlant = Math.round((section.tons * sectionRequirements.powerPlant) / totalRequired);
    } else {
      section.reactionDrive = 0;
      section.jumpDrive = 0;
      section.powerPlant = 0;
    }
    
    section.total = section.reactionDrive + section.jumpDrive + section.powerPlant;

    setFuelDistribution(newDistribution);
  };

  // Apply fuel configuration to all sections
  const applyFuelConfiguration = () => {
    sections.forEach(section => {
      const distribution = fuelDistribution[section.id];
      if (distribution) {
        dispatch(setSectionFuelAllocation({
          sectionId: section.id,
          percentage: distribution.percentage,
          tons: distribution.tons,
          requirements: {
            reactionDrive: distribution.reactionDrive,
            jumpDrive: distribution.jumpDrive,
            powerPlant: distribution.powerPlant,
            total: distribution.total,
          },
        }));
      }
    });
  };

  // Auto-update fuel quantity when auto-calculate is enabled
  useEffect(() => {
    if (!useManualQuantity) {
      // Use the calculated fuel requirements when auto-calculate is enabled
      setTotalFuelTons(fuelRequirements.total);
    }
  }, [fuelRequirements.total, useManualQuantity]);

  if (sections.length === 0) {
    return (
      <div className="text-center py-8">
        <p className="text-gray-600">No breakaway sections configured. Please configure breakaway hulls first.</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="text-center">
        <h2 className="text-2xl font-semibold mb-4 text-gray-800 dark:text-gray-100">
          Breakaway Hull Fuel Configuration
        </h2>
        <p className="text-gray-600 dark:text-gray-300 mb-6">
          Configure fuel allocation for the entire system and distribute across {sections.length} sections
        </p>
      </div>

      {/* Fuel Configuration */}
      <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-6">
        <h3 className="text-lg font-semibold mb-4 text-gray-900 dark:text-gray-100">Fuel Configuration</h3>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Total Fuel Tanks (tons)
            </label>
            <div className="flex items-center space-x-2">
              <input
                type="number"
                min="1"
                value={totalFuelTons}
                onChange={(e) => handleFuelQuantityChange(parseInt(e.target.value) || 1)}
                className="flex-1 p-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-gray-100"
              />
              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={useManualQuantity}
                  onChange={(e) => setUseManualQuantity(e.target.checked)}
                  className="mr-2"
                />
                <span className="text-sm text-gray-600 dark:text-gray-300">Manual</span>
              </label>
            </div>
            {!useManualQuantity && (
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                Auto-calculated based on ship configuration
              </p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Fuel Cost
            </label>
            <div className="p-3 bg-gray-50 dark:bg-gray-700 rounded-md">
              <span className="text-lg font-semibold text-green-600 dark:text-green-400">
                {formatCurrency(totalFuelTons * 1000)} {/* 1000 Cr per ton */}
              </span>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                {formatNumber(totalFuelTons)} tons × 1,000 Cr/ton
              </p>
            </div>
          </div>
        </div>

        {/* Fuel Requirements Display */}
        <div className="bg-gray-50 dark:bg-gray-700 p-4 rounded-md mb-6">
          <h4 className="font-medium mb-3 text-gray-900 dark:text-gray-100">Total System Fuel Requirements</h4>
          <div className="text-xs text-gray-600 dark:text-gray-400 mb-3">
            Based on {weeks} week{weeks !== 1 ? 's' : ''} of operation ({reactionDriveHours} hours)
          </div>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 text-sm">
            <div>
              <span className="font-medium text-gray-700 dark:text-gray-300">Reaction Drive:</span>
              <span className="ml-2 text-gray-900 dark:text-gray-100">{formatNumber(fuelRequirements.reactionDrive)} tons</span>
            </div>
            <div>
              <span className="font-medium text-gray-700 dark:text-gray-300">Jump Drive:</span>
              <span className="ml-2 text-gray-900 dark:text-gray-100">{formatNumber(fuelRequirements.jumpDrive)} tons</span>
            </div>
            <div>
              <span className="font-medium text-gray-700 dark:text-gray-300">Power Plant:</span>
              <span className="ml-2 text-gray-900 dark:text-gray-100">{formatNumber(fuelRequirements.powerPlant)} tons</span>
            </div>
            <div>
              <span className="font-medium text-gray-700 dark:text-gray-300">Total Required:</span>
              <span className="ml-2 text-gray-900 dark:text-gray-100">{formatNumber(fuelRequirements.total)} tons</span>
            </div>
          </div>
        </div>

        {/* Fuel Reserve Configuration Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-6">
          {/* Power Plant Reserve */}
          <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-6">
            <h3 className="text-lg font-semibold text-gray-800 dark:text-gray-100 mb-4">
              Power Plant Reserve
            </h3>
            <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
              Weeks of Power Plant Operation
            </label>
            <div className="flex items-center gap-2">
              <input
                type="number"
                value={powerPlantWeeks}
                min={1}
                onChange={(e) => {
                  const newWeeks = Math.max(1, parseInt(e.target.value) || 1);
                  handlePowerPlantReserveChange(newWeeks);
                }}
                className="w-32 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm dark:bg-gray-700 dark:text-gray-100"
              />
            </div>
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
              {sections.some(s => s.powerPlant) ? 
                `Power plants configured for ${sections.filter(s => s.powerPlant).length} section(s)` : 
                'No power plants configured for sections'
              }
            </p>
            <div className="mt-2 text-sm text-gray-700 dark:text-gray-300">
              Fuel Required:{" "}
              <span className="font-bold">{`${formatNumber(fuelRequirements.powerPlant)} tons`}</span>
            </div>
          </div>

          {/* Jump Drive Reserve */}
          {drives.some(d => d.type === "jump") && (
            <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-6">
              <h3 className="text-lg font-semibold text-gray-800 dark:text-gray-100 mb-4">
                Jump Drive Reserve
              </h3>
              <label className="block text-gray-700 dark:text-gray-300 mb-2">
                Number of Parsecs of Jump Fuel Reserve
              </label>
              <input
                type="number"
                min={1}
                step={1}
                value={jumpDriveReserveParsecs}
                onChange={(e) => {
                  const newParsecs = Math.max(1, parseInt(e.target.value) || 1);
                  handleJumpDriveReserveChange(newParsecs);
                }}
                className="w-32 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-gray-100"
              />
              <p className="text-gray-700 dark:text-gray-300 mt-2 font-semibold">
                Jump Fuel Required (for {drives.find(d => d.type === "jump")?.rating || 1} parsec jump):{" "}
                <span className="font-bold">{`${formatNumber(calculateJumpFuelConsumption(drives.find(d => d.type === "jump")?.rating || 1, totalTonnage))} tons`}</span>
              </p>
              <p className="text-gray-700 dark:text-gray-300 mt-2 font-semibold">
                Total Jump Fuel Allocated (for {jumpDriveReserveParsecs} parsec jump):{" "}
                <span className="font-bold">{`${formatNumber(fuelRequirements.jumpDrive)} tons`}</span>
              </p>
            </div>
          )}

          {/* Reaction Drive Reserve */}
          {drives.some(d => d.type === "reaction") && (
            <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-6">
              <h3 className="text-lg font-semibold text-gray-800 dark:text-gray-100 mb-4">
                Reaction Drive Reserve
              </h3>
              <label className="block text-gray-700 dark:text-gray-300 mb-2">
                Hours of Thruster Operation
              </label>
              <input
                type="number"
                min={1}
                value={reactionDriveHours}
                onChange={(e) => {
                  const newHours = Math.max(1, parseInt(e.target.value) || 1);
                  handleReactionDriveHoursChange(newHours);
                }}
                className="w-32 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-gray-100"
              />
              <p className="text-gray-500 dark:text-gray-400 text-sm mt-2">
                Minimum: 1 hour(s). 2.5% per thrust per hour.
              </p>
              <p className="text-gray-700 dark:text-gray-300 mt-2 font-semibold">
                Fuel Required:{" "}
                <span className="font-bold">{`${formatNumber(fuelRequirements.reactionDrive)} tons`}</span>
              </p>
            </div>
          )}
        </div>

        {/* Fuel Status */}
        <div className={`p-4 rounded-md ${
          totalFuelTons >= fuelRequirements.total 
            ? 'bg-green-50 dark:bg-green-900/30 border border-green-200 dark:border-green-700' 
            : 'bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-700'
        }`}>
          <div className="flex items-center justify-between">
            <div className="text-sm">
              <span className="font-medium text-gray-700 dark:text-gray-300">Fuel Status: </span>
              <span className={totalFuelTons >= fuelRequirements.total ? 'text-green-700 dark:text-green-400' : 'text-red-700 dark:text-red-400'}>
                {totalFuelTons >= fuelRequirements.total ? 'Adequate' : 'Insufficient'}
              </span>
            </div>
            <div className="text-sm">
              <span className="font-medium text-gray-700 dark:text-gray-300">Surplus: </span>
              <span className={totalFuelTons >= fuelRequirements.total ? 'text-green-700 dark:text-green-400' : 'text-red-700 dark:text-red-400'}>
                {formatNumber(totalFuelTons - fuelRequirements.total)} tons
              </span>
            </div>
          </div>
          {totalFuelTons < fuelRequirements.total && (
            <p className="text-xs text-red-600 dark:text-red-400 mt-1">
              Need {formatNumber(fuelRequirements.total - totalFuelTons)} more tons of fuel
            </p>
          )}
        </div>
      </div>

      {/* Fuel Distribution */}
      <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Fuel Distribution</h3>
          <div className="flex items-center space-x-4">
            <div className="flex items-center space-x-2">
              <input
                type="radio"
                id="auto-fuel-distribution"
                name="fuel-distribution-mode"
                checked={distributionMode === 'auto'}
                onChange={() => handleDistributionModeChange('auto')}
                className="mr-2"
              />
              <label htmlFor="auto-fuel-distribution" className="text-sm text-gray-700 dark:text-gray-300">Auto (by size)</label>
            </div>
            <div className="flex items-center space-x-2">
              <input
                type="radio"
                id="manual-fuel-distribution"
                name="fuel-distribution-mode"
                checked={distributionMode === 'manual'}
                onChange={() => handleDistributionModeChange('manual')}
                className="mr-2"
              />
              <label htmlFor="manual-fuel-distribution" className="text-sm text-gray-700 dark:text-gray-300">Manual</label>
            </div>
          </div>
        </div>

        {/* Distribution Status */}
        <div className={`p-3 rounded-md mb-4 ${
          distributionTotals.isValid 
            ? 'bg-green-50 dark:bg-green-900/30 border border-green-200 dark:border-green-700' 
            : 'bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-700'
        }`}>
          <div className="flex items-center justify-between">
            <div className="text-sm">
              <span className="font-medium text-gray-700 dark:text-gray-300">Distribution Status: </span>
              <span className={distributionTotals.isValid ? 'text-green-700 dark:text-green-400' : 'text-red-700 dark:text-red-400'}>
                {distributionTotals.isValid ? 'Valid' : 'Invalid'}
              </span>
            </div>
            <div className="text-sm">
              <span className="font-medium text-gray-700 dark:text-gray-300">Total: </span>
              <span className={distributionTotals.isValid ? 'text-green-700 dark:text-green-400' : 'text-red-700 dark:text-red-400'}>
                {distributionTotals.totalPercentage.toFixed(1)}%
              </span>
            </div>
          </div>
          {!distributionTotals.isValid && (
            <p className="text-xs text-red-600 dark:text-red-400 mt-1">
              {distributionTotals.isOverAllocated 
                ? `Over-allocated by ${(distributionTotals.totalPercentage - 100).toFixed(1)}%`
                : `Under-allocated by ${(100 - distributionTotals.totalPercentage).toFixed(1)}%`
              }
            </p>
          )}
        </div>

        {/* Section Distribution Table */}
        <div className="overflow-x-auto">
          <table className="w-full border-collapse border border-gray-300 dark:border-gray-600">
            <thead>
              <tr className="bg-gray-50 dark:bg-gray-700">
                <th className="border border-gray-300 dark:border-gray-600 px-3 py-2 text-left text-sm font-medium text-gray-900 dark:text-gray-100">Section</th>
                <th className="border border-gray-300 dark:border-gray-600 px-3 py-2 text-left text-sm font-medium text-gray-900 dark:text-gray-100">Size (tons)</th>
                <th className="border border-gray-300 dark:border-gray-600 px-3 py-2 text-left text-sm font-medium text-gray-900 dark:text-gray-100">Size %</th>
                <th className="border border-gray-300 dark:border-gray-600 px-3 py-2 text-left text-sm font-medium text-gray-900 dark:text-gray-100">Fuel %</th>
                <th className="border border-gray-300 dark:border-gray-600 px-3 py-2 text-left text-sm font-medium text-gray-900 dark:text-gray-100">Fuel Tons</th>
                <th className="border border-gray-300 dark:border-gray-600 px-3 py-2 text-left text-sm font-medium text-gray-900 dark:text-gray-100">Reaction</th>
                <th className="border border-gray-300 dark:border-gray-600 px-3 py-2 text-left text-sm font-medium text-gray-900 dark:text-gray-100">Jump</th>
                <th className="border border-gray-300 dark:border-gray-600 px-3 py-2 text-left text-sm font-medium text-gray-900 dark:text-gray-100">Power</th>
                <th className="border border-gray-300 dark:border-gray-600 px-3 py-2 text-left text-sm font-medium text-gray-900 dark:text-gray-100">Total</th>
              </tr>
            </thead>
            <tbody>
              {sections.map(section => {
                const distribution = fuelDistribution[section.id];
                const sizePercentage = (section.size / totalTonnage) * 100;
                
                return (
                  <tr key={section.id} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                    <td className="border border-gray-300 dark:border-gray-600 px-3 py-2 text-sm font-medium text-gray-900 dark:text-gray-100">
                      {section.name}
                    </td>
                    <td className="border border-gray-300 dark:border-gray-600 px-3 py-2 text-sm text-gray-900 dark:text-gray-100">
                      {formatNumber(section.size)}
                    </td>
                    <td className="border border-gray-300 dark:border-gray-600 px-3 py-2 text-sm text-gray-900 dark:text-gray-100">
                      {sizePercentage.toFixed(1)}%
                    </td>
                    <td className="border border-gray-300 dark:border-gray-600 px-3 py-2">
                      {distributionMode === 'manual' ? (
                        <input
                          type="number"
                          min="0"
                          max="100"
                          step="0.1"
                          value={distribution?.percentage.toFixed(1) || '0'}
                          onChange={(e) => handleDistributionChange(section.id, 'percentage', parseFloat(e.target.value) || 0)}
                          className="w-20 p-1 border border-gray-300 dark:border-gray-600 rounded text-sm dark:bg-gray-700 dark:text-gray-100"
                        />
                      ) : (
                        <span className="text-sm text-gray-900 dark:text-gray-100">{distribution?.percentage.toFixed(1)}%</span>
                      )}
                    </td>
                    <td className="border border-gray-300 dark:border-gray-600 px-3 py-2">
                      {distributionMode === 'manual' ? (
                        <input
                          type="number"
                          min="0"
                          max={totalFuelTons}
                          step="1"
                          value={Math.round(distribution?.tons || 0)}
                          onChange={(e) => handleDistributionChange(section.id, 'tons', parseInt(e.target.value) || 0)}
                          className="w-20 p-1 border border-gray-300 dark:border-gray-600 rounded text-sm dark:bg-gray-700 dark:text-gray-100"
                        />
                      ) : (
                        <span className="text-sm text-gray-900 dark:text-gray-100">{formatNumber(Math.round(distribution?.tons || 0))}</span>
                      )}
                    </td>
                    <td className="border border-gray-300 dark:border-gray-600 px-3 py-2 text-sm text-gray-900 dark:text-gray-100">
                      {formatNumber(distribution?.reactionDrive || 0)}
                    </td>
                    <td className="border border-gray-300 dark:border-gray-600 px-3 py-2 text-sm text-gray-900 dark:text-gray-100">
                      {formatNumber(distribution?.jumpDrive || 0)}
                    </td>
                    <td className="border border-gray-300 dark:border-gray-600 px-3 py-2 text-sm text-gray-900 dark:text-gray-100">
                      {formatNumber(distribution?.powerPlant || 0)}
                    </td>
                    <td className="border border-gray-300 dark:border-gray-600 px-3 py-2 text-sm font-medium text-gray-900 dark:text-gray-100">
                      {formatNumber(distribution?.total || 0)}
                    </td>
                  </tr>
                );
              })}
            </tbody>
            <tfoot>
              <tr className="bg-gray-100 dark:bg-gray-600 font-medium">
                <td className="border border-gray-300 dark:border-gray-600 px-3 py-2 text-gray-900 dark:text-gray-100">Total</td>
                <td className="border border-gray-300 dark:border-gray-600 px-3 py-2 text-gray-900 dark:text-gray-100">{formatNumber(totalTonnage)}</td>
                <td className="border border-gray-300 dark:border-gray-600 px-3 py-2 text-gray-900 dark:text-gray-100">100.0%</td>
                <td className="border border-gray-300 dark:border-gray-600 px-3 py-2 text-gray-900 dark:text-gray-100">{distributionTotals.totalPercentage.toFixed(1)}%</td>
                <td className="border border-gray-300 dark:border-gray-600 px-3 py-2 text-gray-900 dark:text-gray-100">{formatNumber(distributionTotals.totalTons)}</td>
                <td className="border border-gray-300 dark:border-gray-600 px-3 py-2 text-gray-900 dark:text-gray-100">{formatNumber(distributionTotals.totalReactionDrive)}</td>
                <td className="border border-gray-300 dark:border-gray-600 px-3 py-2 text-gray-900 dark:text-gray-100">{formatNumber(distributionTotals.totalJumpDrive)}</td>
                <td className="border border-gray-300 dark:border-gray-600 px-3 py-2 text-gray-900 dark:text-gray-100">{formatNumber(distributionTotals.totalPowerPlant)}</td>
                <td className="border border-gray-300 dark:border-gray-600 px-3 py-2 text-gray-900 dark:text-gray-100">{formatNumber(distributionTotals.totalFuel)}</td>
              </tr>
            </tfoot>
          </table>
        </div>

        {/* Apply Configuration Button */}
        <div className="mt-6 text-center">
          <button
            onClick={applyFuelConfiguration}
            disabled={!distributionTotals.isValid}
            className={`px-6 py-3 rounded-md font-semibold transition-colors ${
              distributionTotals.isValid
                ? 'bg-blue-600 text-white hover:bg-blue-700'
                : 'bg-gray-300 dark:bg-gray-600 text-gray-500 dark:text-gray-400 cursor-not-allowed'
            }`}
          >
            Apply Fuel Configuration
          </button>
          {!distributionTotals.isValid && (
            <p className="text-sm text-red-600 dark:text-red-400 mt-2">
              Please ensure fuel distribution equals exactly 100% before applying
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

// Helper function to calculate section fuel requirements
const calculateSectionFuelRequirements = (section: any, weeksValue: number) => {
  let reactionDrive = 0;
  let jumpDrive = 0;
  let powerPlant = 0;

  // Reaction drive fuel
  if (section.drives.reaction) {
    const drive = section.drives.reaction;
    const hoursPerWeek = 168;
    const totalHours = weeksValue * hoursPerWeek; // Use the same weeks as system calculation
    
    if (drive.rating === 0) {
      reactionDrive = 0.25 * totalHours; // 0.25 tons per hour for thrust 0
    } else {
      // 2.5% per thrust per hour of section's tonnage
      reactionDrive = section.size * 0.025 * drive.rating * totalHours;
    }
  }

  // Jump drive fuel
  if (section.drives.jump) {
    const drive = section.drives.jump;
    // 10% of section's tonnage × jump rating × reserve parsecs (default to 1 for sections)
    jumpDrive = calculateJumpFuelConsumption(drive.rating, section.size) * 1;
  }

  // Power plant fuel
  if (section.powerPlant) {
    powerPlant = calculatePowerPlantFuelConsumption(section.powerPlant, weeksValue);
  }

  return {
    reactionDrive: Math.round(reactionDrive),
    jumpDrive: Math.round(jumpDrive),
    powerPlant: Math.round(powerPlant),
    total: Math.round(reactionDrive + jumpDrive + powerPlant),
  };
};
